import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Grid, Paper, Typography, Button, Stack } from '@mui/material';
import SEO from '../components/SEO';
import { Link as RouterLink } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import ImageSlider from '../components/ImageSlider';
import InfoIcon from '@mui/icons-material/Info';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import PaymentIcon from '@mui/icons-material/Payment';

import CelebrationIcon from '@mui/icons-material/Celebration';
import LandscapeIcon from '@mui/icons-material/Landscape';
import TempleIcon from '@mui/icons-material/TempleBuddhist';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import HikingIcon from '@mui/icons-material/Hiking';
import MuseumIcon from '@mui/icons-material/Museum';
import PhoneIcon from '@mui/icons-material/Phone';

import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventIcon from '@mui/icons-material/Event';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CampaignIcon from '@mui/icons-material/Campaign';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GavelIcon from '@mui/icons-material/Gavel';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ConstructionIcon from '@mui/icons-material/Construction';
import FestivalIcon from '@mui/icons-material/Festival';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SectionOrnament from '../components/SectionOrnament';
import BusinessPromotions from '../components/BusinessPromotions';
import { useLanguage } from '../context/LanguageContext';



// Pooja quick-access tiles
const poojaTiles = [
  {
    title: 'Daily Poojas',
    description: 'All nithya sevas performed every day at the temple.',
    to: '/poojas/daily',
    color: 'linear-gradient(135deg, #e8fff2, #ccf5e0)',
  },
  {
    title: 'Special Poojas',
    description: 'Unique sevas for specific occasions and personal offerings.',
    to: '/poojas/special',
    color: 'linear-gradient(135deg, #f3f6ff, #e8ecff)',
  },
  {
    title: 'Festival Poojas',
    description: 'Seasonal utsavams and festival-day pooja schedules.',
    to: '/poojas/festival',
    color: 'linear-gradient(135deg, #ffe1e7, #ffd6a5)',
  },
  {
    title: 'Vazhipad List',
    description: 'Complete vazhipad catalogue with descriptions and dakshina.',
    to: '/poojas/vazhipad',
    color: 'linear-gradient(135deg, #fef3d9, #ffe8ba)',
  },
];

// Animated section wrapper component with staggered children animation
function AnimatedSection({ children, delay = 0, staggerChildren = false }: { children: React.ReactNode; delay?: number; staggerChildren?: boolean }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setTimeout(() => setIsVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -80px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    const currentRef = ref.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay, isVisible]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
        filter: isVisible ? 'blur(0px)' : 'blur(2px)',
        transition: 'opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), filter 0.35s ease-out',
        willChange: 'opacity, transform, filter',
        ...(staggerChildren && {
          '& > *': {
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
            filter: isVisible ? 'blur(0px)' : 'blur(2px)',
            transition: 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), filter 0.3s ease-out',
            willChange: 'opacity, transform, filter',
          },
          '& > *:nth-of-type(1)': { transitionDelay: '0s' },
          '& > *:nth-of-type(2)': { transitionDelay: '0.05s' },
          '& > *:nth-of-type(3)': { transitionDelay: '0.1s' },
          '& > *:nth-of-type(4)': { transitionDelay: '0.15s' },
          '& > *:nth-of-type(5)': { transitionDelay: '0.2s' },
          '& > *:nth-of-type(6)': { transitionDelay: '0.25s' },
          '& > *:nth-of-type(7)': { transitionDelay: '0.3s' },
          '& > *:nth-of-type(8)': { transitionDelay: '0.35s' },
        }),
      }}
    >
      {children}
    </Box>
  );
}

// Animated content component for text and images within sections
function AnimatedContent({ children, delay = 0, direction = 'left' }: { children: React.ReactNode; delay?: number; direction?: 'left' | 'right' | 'up' | 'down' }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    const currentRef = ref.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay, isVisible]);

  const hiddenTransform = {
    left: 'translate(-20px, 0) scale(0.98)',
    right: 'translate(20px, 0) scale(0.98)',
    up: 'translate(0, -18px) scale(0.98)',
    down: 'translate(0, 18px) scale(0.98)',
  };

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0, 0) scale(1)' : hiddenTransform[direction],
        filter: isVisible ? 'blur(0px)' : 'blur(2px)',
        transition: `opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${Math.round(delay * 0.08)}ms, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${Math.round(delay * 0.08)}ms, filter 0.3s ease-out ${Math.round(delay * 0.08)}ms`,
        willChange: 'opacity, transform, filter',
      }}
    >
      {children}
    </Box>
  );
}

export default function Home() {
  const { t } = useLanguage();

  const [aboutContent, setAboutContent] = useState({
    title: t('home.aboutIntro'),
    subtitle: t('home.aboutTemple'),
    description: t('home.aboutDescription'),
    imageUrl: '/assets/header_god_image.png'
  });

  const [poojaSection, setPoojaSection] = useState({
    title: 'Plan Your Devotional Day',
    subtitle: 'POOJA CENTRE',
    description: 'Browse pooja categories and book your offerings online with instant confirmation.',
    buttonText: 'Book a Pooja',
    buttonLink: '/poojas/booking',
    secondButtonText: 'View Vazhipads',
    secondButtonLink: '/poojas/vazhipad',
    tiles: poojaTiles
  });

  const [newsSection, setNewsSection] = useState({
    subtitle: 'NEWS OVERVIEW',
    title: 'Temple Announcements & Updates',
    description: 'Stay informed on announcements, upcoming events, notices, and recent news.',
    tiles: [
      { title: 'Announcements', description: 'Latest official temple announcements and bulletin items.', link: '/news/announcements', chip: 'New' },
      { title: 'News', description: 'Coverage and updates about temple activities and programs.', link: '/news/news', chip: 'Recent' },
      { title: 'Upcoming', description: 'Dates and details for scheduled poojas, utsavams, and events.', link: '/news/upcoming', chip: 'Schedule' },
      { title: 'Notices', description: 'Important notices and office circulars for devotees.', link: '/news/notices', chip: 'Official' }
    ]
  });

  const [donationSection, setDonationSection] = useState({
    subtitle: 'DONATION OVERVIEW',
    title: 'Support Temple Causes',
    description: 'Your contributions help maintain our sacred traditions and serve the community',
    tiles: [
      { title: 'Online Donation', description: 'Quick, secure contributions with instant receipt.', link: '/donate/online', tag: 'Popular' },
      { title: 'Annadanam', description: 'Sacred food offering during Ulsav celebrations.', link: '/donate/annadanam', tag: 'Ulsav' },
      { title: 'Renovation', description: 'Help preserve and improve temple infrastructure.', link: '/donate/renovation', tag: 'Urgent' },
      { title: 'Festival Support', description: 'Sponsor arrangements for seasonal utsavams.', link: '/donate/festival', tag: 'Seasonal' }
    ]
  });

  const [gallerySection, setGallerySection] = useState({
    subtitle: 'GALLERY OVERVIEW',
    title: 'Explore Photos & Videos',
    description: 'A glimpse of temple moments — festivals, premises, events and serene views.',
  });

  const [galleryPhotos, setGalleryPhotos] = useState<{ id: string; title: string; imageUrl: string; category?: string; order?: number }[]>([]);
  const [galleryVideos, setGalleryVideos] = useState<{ id: string; title: string; videoUrl: string; description?: string }[]>([]);

  const [contactSection, setContactSection] = useState({
    subtitle: 'CONTACT OVERVIEW',
    title: 'Get In Touch With Us',
    description: 'Find office details, map, general info, and share feedback easily.',
    tiles: [
      { title: 'Map', description: 'Locate the temple and plan your route.', link: '/contact/map' },
      { title: 'Info', description: 'General information for visitors.', link: '/contact/info' },
      { title: 'Feedback', description: 'Share suggestions and experiences.', link: '/contact/feedback' }
    ]
  });

  const [nearbySection, setNearbySection] = useState({
    subtitle: 'NEARBY OVERVIEW',
    title: 'Explore Nearby Attractions',
    description: 'Beaches, temples, viewpoints and heritage spots around the temple.',
    tiles: [
      { title: 'Beaches', description: 'Relax at serene shores and coastal walks.', link: '/nearby/beaches', chip: 'Scenic', imageUrl: 'https://crm.flightsmojo.in//UploadfileblogContent/71132433-549f-454f-9ac3-30ecf2ac6bdb.jpg' },
      { title: 'Temples', description: 'Visit historic shrines and spiritual centers.', link: '/nearby/temples', chip: 'Pilgrimage', imageUrl: 'https://www.templepurohit.com/wp-content/uploads/2015/11/DSC_1012-e1482403088424.jpg' },
      { title: 'Viewpoints', description: 'Catch sunset vistas and hilltop views.', link: '/nearby/viewpoints', chip: 'Panorama', imageUrl: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2f/17/1b/98/caption.jpg?w=1100&h=-1&s=1' },
      { title: 'Heritage', description: 'Discover museums and cultural landmarks.', link: '/nearby/heritage', chip: 'Culture', imageUrl: 'https://www.keralatourism.org/images/enchanting_kerala/large/en_ooru_tribal_heritage_village20221206093016_1251_1.jpg' },
      { title: 'Boating', description: 'Enjoy backwater rides and boat tours.', link: '/nearby/boating', chip: 'Leisure', imageUrl: 'https://img.mathrubhumi.com/view/acePublic/alias/contentid/1lwgsfedrrwuduheunn/0/akalappuzha.webp?f=3%3A2&q=0.75&w=900' },
      { title: 'Activities', description: 'Try hiking trails and outdoor fun.', link: '/nearby/activities', chip: 'Outdoor', imageUrl: 'https://images.squarespace-cdn.com/content/v1/5238f4e6e4b02e747c5476e2/1534176133795-EJZ3ZCPRGJTIRFZ0IP8W/hells+canyon+rafting+trip.jpg?format=2500w' }
    ]
  });

  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'about_section');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAboutContent({
            title: data.title || t('home.aboutIntro'),
            subtitle: data.subtitle || t('home.aboutTemple'),
            description: data.description || t('home.aboutDescription'),
            imageUrl: data.imageUrl || '/assets/header_god_image.png'
          });
        }
      } catch (error) {
        console.error('Error fetching about content:', error);
      }
    };

    const fetchPoojaSection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'pooja_section');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPoojaSection(prev => ({
            title: data.title || prev.title,
            subtitle: data.subtitle || prev.subtitle,
            description: data.description || prev.description,
            buttonText: data.buttonText || prev.buttonText,
            buttonLink: data.buttonLink || prev.buttonLink,
            secondButtonText: data.secondButtonText || prev.secondButtonText,
            secondButtonLink: data.secondButtonLink || prev.secondButtonLink,
            tiles: data.tiles || poojaTiles
          }));
        }
      } catch (error) {
        console.error('Error fetching pooja section:', error);
      }
    };

    const fetchNewsSection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'news_section');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setNewsSection(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error fetching news section:', error);
      }
    };

    const fetchDonationSection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'donation_section');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setDonationSection(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error fetching donation section:', error);
      }
    };

    const fetchGallerySection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'gallery_section');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setGallerySection(prev => ({ ...prev, subtitle: data.subtitle || prev.subtitle, title: data.title || prev.title, description: data.description || prev.description }));
        }
      } catch (error) {
        console.error('Error fetching gallery section:', error);
      }
    };

    const fetchGalleryPhotos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'gallery_photos'));
        const fetched: any[] = [];
        querySnapshot.forEach((d) => {
          const data = d.data();
          if (data.imageUrl) fetched.push({ id: d.id, ...data });
        });
        setGalleryPhotos(fetched.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } catch (error) {
        console.error('Error fetching gallery photos:', error);
      }
    };

    const fetchGalleryVideos = async () => {
      try {
        const docRef = doc(db, 'gallery_content', 'videos');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setGalleryVideos((data.videos || []).filter((v: any) => v.videoUrl));
        }
      } catch (error) {
        console.error('Error fetching gallery videos:', error);
      }
    };

    const fetchContactSection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'contact_section');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContactSection(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error fetching contact section:', error);
      }
    };

    const fetchNearbySection = async () => {
      try {
        const docRef = doc(db, 'homepage_content', 'nearby_section');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setNearbySection(prev => ({
            ...prev,
            ...data,
            tiles: (data.tiles || prev.tiles).map((tile: any, idx: number) => ({
              ...prev.tiles[idx],
              ...tile,
            })),
          }));
        }
      } catch (error) {
        console.error('Error fetching nearby section:', error);
      }
    };

    // Fetch all homepage data in parallel for faster loading
    Promise.all([
      fetchAboutContent(),
      fetchPoojaSection(),
      fetchNewsSection(),
      fetchDonationSection(),
      fetchGallerySection(),
      fetchGalleryPhotos(),
      fetchGalleryVideos(),
      fetchContactSection(),
      fetchNearbySection(),
    ]).catch(() => { /* individual errors already logged */ });
  }, [t]);
  
  return (
    <Box 
      sx={{ 
        mt: 0, 
        pt: 0, 
        pb: 6,
        overflowX: { xs: 'clip', md: 'visible' },
      }}
    >
      <SEO
        title="Kottayil Temple Muchukunnu — Ancient Shiva Temple, Kozhikode Kerala"
        description="Kottayil Temple (Kottayil Shiva Temple) Muchukunnu, Kozhikode, Kerala — one of the most ancient and sacred Hindu temples in Malabar. Famous for Kottayil Kulam (temple pond), Sree Bhadrakali sanctum, daily poojas, vazhipad booking, annadanam, and grand festivals. Visit or book pooja online."
        path="/"
        keywords="Kottayil temple, Kottayil temple Muchukunnu, Kottayil Shiva temple, Kottayil Shiva temple Muchukunnu, Kottayil kulam, Kottayil pond, famous temple pond Kerala, Muchukunnu temple, Muchukunnu Shiva temple, Kotta-Kovilakam, Kotta Kovilakam Kshethram, Kozhikode temple, Malabar temple, Kerala temple, Sree Bhadrakali, Hindu temple Kerala, sacred pond Kerala, temple tank Kerala, ancient temple Kerala, temple near Kozhikode, temple near Calicut, Koyilandy temple, pooja booking online, vazhipad online, annadanam, temple festivals Kerala, കൊട്ടയിൽ ക്ഷേത്രം, കൊട്ടയിൽ ശിവ ക്ഷേത്രം, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന്, മുച്ചുകുന്ന് ക്ഷേത്രം, കൊട്ടയിൽ കുളം, കേരള ക്ഷേത്രം, വഴിപാട്, പൂജ, അന്നദാനം, കൊട്ട കൊവിലാകം, കോഴിക്കോട് ക്ഷേത്രം, ഉത്സവം, മലബാർ ക്ഷേത്രം"
      />
      <ImageSlider />
      <Container maxWidth={false} disableGutters sx={{ maxWidth: 1450, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  ABOUT TEMPLE — Dark immersive hero with split layout     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={0}>
      <Box
        sx={{
          mt: 4,
          mb: 8,
          borderRadius: { xs: 4, md: 5 },
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          position: 'relative',
          boxShadow: '0 30px 80px rgba(0,0,0,0.2)',
        }}
      >
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.12), transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.08), transparent 70%)', pointerEvents: 'none' }} />

        <Grid container alignItems="stretch">
          {/* Image side */}
          <Grid item xs={12} md={5} sx={{ position: 'relative', minHeight: { xs: 260, md: 'auto' } }}>
            <Box
              component="img"
              src={
                aboutContent.imageUrl
                  ? (aboutContent.imageUrl.startsWith('data:') || aboutContent.imageUrl.startsWith('http')
                      ? aboutContent.imageUrl
                      : `${process.env.PUBLIC_URL}${aboutContent.imageUrl}`)
                  : `${process.env.PUBLIC_URL}/assets/header_god_image.png`
              }
              alt="Temple"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', position: { md: 'absolute' }, inset: 0, minHeight: { xs: 260, md: 780 } }}
            />
            <Box sx={{ position: 'absolute', inset: 0, background: { xs: 'linear-gradient(to bottom, transparent 40%, #1a1a2e)', md: 'linear-gradient(to right, transparent 30%, #1a1a2e 92%)' } }} />
            <Box sx={{
              position: 'absolute', top: 16, left: 16, px: 2.5, py: 0.8,
              background: 'linear-gradient(135deg, #d4af37, #e5c158)',
              color: '#000', fontWeight: 900, fontSize: 12, letterSpacing: 2, borderRadius: 2,
              boxShadow: '0 4px 16px rgba(212,175,55,0.5)',
            }}>
              TEMPLE
            </Box>
          </Grid>

          {/* Content side */}
          <Grid item xs={12} md={7}>
            <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 6, md: 10 }, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: 780 } }}>
                <AnimatedContent delay={100} direction="right">
                <Typography sx={{ color: '#d4af37', fontWeight: 800, letterSpacing: 4, fontSize: 12, mb: 1.5, textTransform: 'uppercase' }}>
                  {aboutContent.subtitle}
                </Typography>
                </AnimatedContent>
                <AnimatedContent delay={250} direction="right">
                <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', mb: 3, lineHeight: 1.15, fontSize: { xs: '1.75rem', md: '2.6rem' } }}>
                  {aboutContent.title}
                </Typography>
                </AnimatedContent>
                <AnimatedContent delay={350} direction="right">
                <Box sx={{ width: 50, height: 3, bgcolor: '#d4af37', borderRadius: 2, mb: 3 }} />
                </AnimatedContent>
                <AnimatedContent delay={450} direction="right">
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.9, fontSize: { xs: '0.9rem', md: '1.05rem' }, mb: 5, maxWidth: 540 }}>
                  {aboutContent.description}
                </Typography>
                </AnimatedContent>

              {/* Feature pills */}
                <AnimatedContent delay={550} direction="up">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 5 }}>
                  {[
                    { label: 'Daily Rituals', icon: <TempleHinduIcon sx={{ fontSize: 15 }} /> },
                    { label: 'Festival Utsavams', icon: <AnnouncementIcon sx={{ fontSize: 15 }} /> },
                    { label: 'Community Seva', icon: <VolunteerActivismIcon sx={{ fontSize: 15 }} /> },
                    { label: 'Historic Legacy', icon: <InfoIcon sx={{ fontSize: 15 }} /> },
                  ].map((f) => (
                    <Box key={f.label} sx={{
                      display: 'flex', alignItems: 'center', gap: 0.8, px: 1.8, py: 0.7,
                      borderRadius: 8, border: '1px solid rgba(212,175,55,0.35)',
                      background: 'rgba(212,175,55,0.08)', backdropFilter: 'blur(6px)',
                      transition: 'all 0.25s', cursor: 'default',
                      '&:hover': { background: 'rgba(212,175,55,0.18)', borderColor: '#d4af37' },
                    }}>
                      <Box sx={{ color: '#d4af37' }}>{f.icon}</Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 600 }}>{f.label}</Typography>
                    </Box>
                  ))}
                </Box>
                </AnimatedContent>

              {/* CTAs */}
                <AnimatedContent delay={650} direction="up">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button component={RouterLink} to="/about" variant="contained" size="large" sx={{
                    bgcolor: '#d4af37', color: '#000', fontWeight: 800, px: 4, py: 1.4, borderRadius: 2,
                    fontSize: '0.9rem', letterSpacing: 1, textTransform: 'uppercase',
                    boxShadow: '0 4px 20px rgba(212,175,55,0.4)',
                    '&:hover': { bgcolor: '#e5c158', transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(212,175,55,0.5)' },
                  }}>
                    Learn More
                  </Button>
                  <Button component={RouterLink} to="/gallery/photos" variant="outlined" size="large" sx={{
                    color: '#fff', borderColor: 'rgba(255,255,255,0.25)', borderWidth: 2, fontWeight: 700,
                    px: 4, py: 1.4, borderRadius: 2, letterSpacing: 1, textTransform: 'uppercase',
                    '&:hover': { borderWidth: 2, borderColor: '#d4af37', color: '#d4af37', bgcolor: 'transparent' },
                  }}>
                    View Gallery →
                  </Button>
                </Stack>
                </AnimatedContent>
            </Box>
          </Grid>
        </Grid>
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  POOJA CENTRE — Deep wine sanctum with camphor glow       */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={50} staggerChildren={true}>
      <Box sx={{
        mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(160deg, #1a0012 0%, #2d0a1e 30%, #3d1228 55%, #2a0818 80%, #150010 100%)',
        boxShadow: '0 30px 80px rgba(26,0,18,0.45)',
      }}>
        {/* Sanctum ambient glows */}
        <Box sx={{ position: 'absolute', top: -100, right: -80, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,112,67,0.1), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -90, left: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(233,30,99,0.06), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '35%', left: '50%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,171,145,0.04), transparent 50%)', pointerEvents: 'none', transform: 'translateX(-50%)' }} />

        {/* Top sacred band — kumkum pattern */}
        <Box sx={{ height: 5, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', background: 'repeating-linear-gradient(90deg, #ff7043 0px, #ff7043 6px, transparent 6px, transparent 10px, #e91e63 10px, #e91e63 16px, transparent 16px, transparent 20px, #ffab91 20px, #ffab91 26px, transparent 26px, transparent 30px)', opacity: 0.5 }} />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #1a0012, transparent 12%, transparent 88%, #1a0012)' }} />
        </Box>

        {/* Floating camphor sparks */}
        {[
          { top: 40, left: 70, delay: '0s' },
          { top: 80, right: 90, delay: '1.2s' },
          { bottom: 100, left: '25%', delay: '0.6s' },
          { top: 110, right: '30%', delay: '1.8s' },
          { bottom: 70, right: 60, delay: '2.4s' },
        ].map((s, idx) => (
          <Box key={`cs-${idx}`} sx={{
            position: 'absolute', top: s.top, right: s.right, bottom: s.bottom, left: s.left,
            width: 4, height: 4, borderRadius: '50%',
            background: 'radial-gradient(circle, #fff 10%, #ffab91 50%, transparent 100%)',
            boxShadow: '0 0 6px #ffab91, 0 0 12px rgba(255,171,145,0.3)',
            animation: `camphorSpark ${2.5 + idx * 0.3}s ease-in-out infinite`,
            animationDelay: s.delay, pointerEvents: 'none',
            '@keyframes camphorSpark': {
              '0%, 100%': { opacity: 0.2, transform: 'scale(0.8) translateY(0)' },
              '40%': { opacity: 1, transform: 'scale(1.5) translateY(-12px)' },
              '70%': { opacity: 0.4, transform: 'scale(1) translateY(-6px)' },
            },
          }} />
        ))}

        {/* Sacred ॐ watermarks */}
        {[
          { top: 60, right: 50, size: 80, opacity: 0.02 },
          { bottom: 60, left: 40, size: 100, opacity: 0.015 },
        ].map((o, idx) => (
          <Box key={`po-${idx}`} sx={{
            position: 'absolute', top: o.top, right: o.right, bottom: o.bottom, left: o.left,
            fontSize: o.size, color: '#ff7043', opacity: o.opacity, fontWeight: 900,
            pointerEvents: 'none', userSelect: 'none', lineHeight: 1,
            animation: `pOmSway ${10 + idx * 3}s ease-in-out infinite`,
            '@keyframes pOmSway': { '0%, 100%': { transform: 'rotate(0deg)' }, '50%': { transform: 'rotate(6deg)' } },
          }}>ॐ</Box>
        ))}

        {/* ===== HEADER — Centered ===== */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 4, md: 5 }, pb: { xs: 2, md: 2.5 }, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <SectionOrnament variant="om" opacity={0.02} size={100} color="#ff7043" repeat={3} offset={-80} blendMode="normal" />

          <AnimatedContent delay={100}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2,
              px: 2.5, py: 0.8, borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(255,112,67,0.1), rgba(255,112,67,0.03))',
              border: '1px solid rgba(255,112,67,0.25)',
            }}>
              <TempleHinduIcon sx={{ color: '#ff7043', fontSize: 16 }} />
              <Typography sx={{ color: '#ff7043', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
                {poojaSection.subtitle}
              </Typography>
            </Box>
          </AnimatedContent>

          <AnimatedContent delay={250}>
            <Typography variant="h3" sx={{
              fontWeight: 900, mb: 1.5, lineHeight: 1.15,
              fontSize: { xs: '1.6rem', md: '2.3rem' },
              background: 'linear-gradient(135deg, #fff 0%, #ffccbc 40%, #ff7043 80%, #e91e63 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {poojaSection.title}
            </Typography>
          </AnimatedContent>

          <AnimatedContent delay={400}>
            <Typography sx={{ color: 'rgba(255,204,188,0.55)', maxWidth: 560, mx: 'auto', lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>
              {poojaSection.description}
            </Typography>
          </AnimatedContent>

          {/* Centre divider */}
          <AnimatedContent delay={500}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1, maxWidth: 200, mx: 'auto' }}>
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, #ff7043)' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e91e63', boxShadow: '0 0 8px rgba(233,30,99,0.5)' }} />
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #ff7043, transparent)' }} />
            </Box>
          </AnimatedContent>
        </Box>

        {/* ===== POOJA CATEGORY CARDS — 4 in a row ===== */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
          <Grid container spacing={2}>
            {poojaSection.tiles.map((tile, i) => {
              const cardThemes = [
                { gradient: 'linear-gradient(135deg, #ff7043, #f4511e)', accent: '#ff7043', glow: 'rgba(255,112,67,0.35)', iconBg: 'rgba(255,112,67,0.15)', borderAccent: 'rgba(255,112,67,0.2)', textMuted: '#ffccbc' },
                { gradient: 'linear-gradient(135deg, #e91e63, #c2185b)', accent: '#f06292', glow: 'rgba(233,30,99,0.3)', iconBg: 'rgba(240,98,146,0.15)', borderAccent: 'rgba(240,98,146,0.2)', textMuted: '#f8bbd0' },
                { gradient: 'linear-gradient(135deg, #ffab91, #ff8a65)', accent: '#ffab91', glow: 'rgba(255,171,145,0.3)', iconBg: 'rgba(255,171,145,0.15)', borderAccent: 'rgba(255,171,145,0.2)', textMuted: '#fbe9e7' },
                { gradient: 'linear-gradient(135deg, #ff6e40, #ff3d00)', accent: '#ff6e40', glow: 'rgba(255,110,64,0.3)', iconBg: 'rgba(255,110,64,0.15)', borderAccent: 'rgba(255,110,64,0.2)', textMuted: '#ffccbc' },
              ];
              const TileIcons = [LocalFireDepartmentIcon, AutoAwesomeIcon, EventIcon, MenuBookIcon];
              const TileIcon = TileIcons[i] || TempleHinduIcon;
              const tc = cardThemes[i] || cardThemes[0];
              return (
                <Grid item xs={12} sm={6} md={3} key={tile.title || i}>
                  <Paper
                    elevation={0}
                    component={RouterLink}
                    to={(tile as any).link || tile.to}
                    sx={{
                      p: 0, borderRadius: 4, overflow: 'hidden', height: '100%',
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${tc.borderAccent}`,
                      backdropFilter: 'blur(12px)',
                      textDecoration: 'none', display: 'flex', flexDirection: 'column',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: `linear-gradient(180deg, ${tc.iconBg} 0%, transparent 40%)`,
                        pointerEvents: 'none', opacity: 0.5,
                      },
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: `0 20px 50px ${tc.glow}`,
                        borderColor: `${tc.accent}60`,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '& .pooja-icon-ring': { transform: 'scale(1.15) rotate(10deg)', boxShadow: `0 0 30px ${tc.glow}` },
                        '& .pooja-arrow': { transform: 'translateX(4px)', opacity: 1 },
                        '& .pooja-glow-line': { opacity: 0.8, width: '60%' },
                      },
                    }}
                  >
                    {/* Top glow accent */}
                    <Box sx={{ height: 3, background: tc.gradient, boxShadow: `0 2px 12px ${tc.glow}` }} />

                    <Box sx={{ p: { xs: 2.5, md: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      {/* Icon ring */}
                      <Box className="pooja-icon-ring" sx={{
                        width: 68, height: 68, borderRadius: '50%', mb: 2, mt: 1,
                        background: `linear-gradient(135deg, ${tc.iconBg}, rgba(255,255,255,0.03))`,
                        border: `2px solid ${tc.borderAccent}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&::after': {
                          content: '""', position: 'absolute', inset: -4, borderRadius: '50%',
                          border: `1px dashed ${tc.accent}20`,
                        },
                      }}>
                        <TileIcon sx={{ color: tc.accent, fontSize: 28, filter: `drop-shadow(0 0 4px ${tc.glow})` }} />
                      </Box>

                      {/* Title */}
                      <Typography sx={{
                        fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', mb: 0.8,
                        letterSpacing: 0.3,
                      }}>
                        {tile.title}
                      </Typography>

                      {/* Description */}
                      <Typography sx={{
                        color: 'rgba(255,204,188,0.55)', fontSize: '0.92rem', lineHeight: 1.7,
                        mb: 2, flex: 1,
                      }}>
                        {tile.description}
                      </Typography>

                      {/* Bottom glow line */}
                      <Box className="pooja-glow-line" sx={{
                        width: '30%', height: 2, borderRadius: 1, mb: 1.5,
                        background: tc.gradient, opacity: 0.4,
                        transition: 'all 0.4s ease',
                        boxShadow: `0 0 8px ${tc.glow}`,
                      }} />

                      {/* Explore link */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ color: tc.accent, fontSize: '0.85rem', fontWeight: 700, letterSpacing: 0.5 }}>
                          Explore
                        </Typography>
                        <Box className="pooja-arrow" sx={{
                          color: tc.accent, fontSize: 14, transition: 'all 0.3s ease', opacity: 0.6,
                          display: 'flex', alignItems: 'center',
                        }}>→</Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* ===== CTA STRIP ===== */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 4, md: 5 } }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: { xs: 2, md: 3 }, flexWrap: 'wrap',
            px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 },
            borderRadius: 4, position: 'relative', overflow: 'hidden',
            bgcolor: 'rgba(255,112,67,0.04)', border: '1px solid rgba(255,112,67,0.12)',
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1,
              background: 'linear-gradient(90deg, transparent 10%, rgba(255,112,67,0.2) 50%, transparent 90%)',
            },
          }}>
            <Box sx={{ textAlign: 'center', flex: 1, minWidth: 200 }}>
              <Typography sx={{ color: 'rgba(255,204,188,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', mb: 0.5 }}>
                Ready to begin?
              </Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.1rem' },
                background: 'linear-gradient(135deg, #fff, #ffab91)',
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Book your pooja offering today
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component={RouterLink} to={poojaSection.buttonLink} variant="contained" sx={{
                background: 'linear-gradient(135deg, #ff7043, #f4511e)',
                color: '#fff', fontWeight: 800, px: 3.5, py: 1.2, borderRadius: 2.5,
                boxShadow: '0 4px 20px rgba(255,112,67,0.3)',
                textTransform: 'none', fontSize: '0.9rem',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(255,112,67,0.4)' },
              }}>
                {poojaSection.buttonText}
              </Button>
              <Button component={RouterLink} to={poojaSection.secondButtonLink} variant="outlined" sx={{
                color: '#ffab91', borderColor: 'rgba(255,171,145,0.3)', fontWeight: 700, borderWidth: 2,
                textTransform: 'none', fontSize: '0.9rem', borderRadius: 2.5,
                '&:hover': { borderColor: '#ff7043', borderWidth: 2, bgcolor: 'rgba(255,112,67,0.08)' },
              }}>
                {poojaSection.secondButtonText}
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Bottom sacred border — kumkum & saffron */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #ff7043, #e91e63, #ffab91, #e91e63, #ff7043, transparent)', opacity: 0.45 }} />
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  NEWS OVERVIEW — Sacred temple palette with warm tones     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={100} staggerChildren={true}>
      <Box sx={{
        mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(160deg, #3e1f0d 0%, #5c2e14 30%, #4a1e0e 60%, #2d1208 100%)',
        boxShadow: '0 30px 80px rgba(62,31,13,0.35)',
      }}>
        {/* Sacred ambient glows — saffron & gold */}
        <Box sx={{ position: 'absolute', top: -80, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.12), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -80, width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(230,81,0,0.08), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '55%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.05), transparent 55%)', pointerEvents: 'none' }} />

        {/* Decorative temple rangoli-style border — top */}
        <Box sx={{ height: 5, background: 'repeating-linear-gradient(90deg, #d4af37 0px, #d4af37 20px, #e65100 20px, #e65100 40px, #c62828 40px, #c62828 60px, #d4af37 60px)', opacity: 0.7 }} />

        {/* Floating Om motifs */}
        {[
          { top: 40, right: 50, opacity: 0.06, size: 60 },
          { bottom: 60, left: 40, opacity: 0.04, size: 80 },
          { top: '50%', right: '20%', opacity: 0.03, size: 100 },
        ].map((om, idx) => (
          <Box key={idx} sx={{
            position: 'absolute', top: om.top, right: om.right, bottom: om.bottom, left: om.left,
            width: om.size, height: om.size, opacity: om.opacity,
            fontSize: om.size * 0.7, color: '#d4af37', fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: `omFloat${idx} ${6 + idx * 2}s ease-in-out infinite`,
            pointerEvents: 'none', userSelect: 'none',
            '@keyframes omFloat0': { '0%, 100%': { transform: 'translateY(0) rotate(0deg)' }, '50%': { transform: 'translateY(-10px) rotate(5deg)' } },
            '@keyframes omFloat1': { '0%, 100%': { transform: 'translateY(0) rotate(0deg)' }, '50%': { transform: 'translateY(-8px) rotate(-3deg)' } },
            '@keyframes omFloat2': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-6px)' } },
          }}>
            ॐ
          </Box>
        ))}

        {/* Floating deepam flame particles */}
        {[
          { top: 25, left: 80, delay: '0s' },
          { top: 60, right: 100, delay: '1s' },
          { bottom: 80, left: '40%', delay: '0.5s' },
          { top: 100, left: '60%', delay: '1.5s' },
          { bottom: 40, right: 60, delay: '0.8s' },
        ].map((p, idx) => (
          <Box key={`flame-${idx}`} sx={{
            position: 'absolute', ...p, width: 4, height: 4,
            background: 'radial-gradient(circle, #ffb300, #ff6f00)',
            borderRadius: '50%', boxShadow: '0 0 8px #ffb300, 0 0 16px rgba(255,179,0,0.4)',
            animation: `deepam ${2.5 + idx * 0.3}s ease-in-out infinite`,
            animationDelay: p.delay, pointerEvents: 'none',
            '@keyframes deepam': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(1) translateY(0)' },
              '50%': { opacity: 0.9, transform: 'scale(1.5) translateY(-12px)' },
            },
          }} />
        ))}

        {/* Header area */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 3.5, md: 4.5 }, pb: { xs: 2, md: 2.5 }, position: 'relative', zIndex: 1 }}>
          <SectionOrnament variant="om" opacity={0.04} size={110} color="#d4af37" repeat={3} offset={-80} blendMode="normal" />
          <AnimatedContent delay={100} direction="right">
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 1.5,
              px: 2, py: 0.6, borderRadius: 8,
              border: '1px solid rgba(212,175,55,0.3)', bgcolor: 'rgba(212,175,55,0.06)',
            }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                background: 'radial-gradient(circle, #ffb300, #d4af37)',
                boxShadow: '0 0 10px rgba(212,175,55,0.7), 0 0 20px rgba(212,175,55,0.3)',
                animation: 'vilakku 2s ease-in-out infinite',
                '@keyframes vilakku': { '0%, 100%': { opacity: 1, boxShadow: '0 0 10px rgba(212,175,55,0.7)' }, '50%': { opacity: 0.6, boxShadow: '0 0 6px rgba(212,175,55,0.4)' } },
              }} />
              <Typography sx={{ color: '#d4af37', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
                {newsSection.subtitle}
              </Typography>
            </Box>
          </AnimatedContent>
          <AnimatedContent delay={250} direction="right">
            <Typography variant="h3" sx={{
              fontWeight: 900, mb: 1, lineHeight: 1.2,
              fontSize: { xs: '1.6rem', md: '2.2rem' },
              background: 'linear-gradient(135deg, #fff 0%, #ffe0b2 60%, #d4af37 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {newsSection.title}
            </Typography>
          </AnimatedContent>
          <AnimatedContent delay={400} direction="right">
            <Typography sx={{ color: 'rgba(255,224,178,0.6)', mb: 1, maxWidth: 500, fontSize: { xs: '0.9rem', md: '1.05rem' }, lineHeight: 1.8 }}>
              {newsSection.description}
            </Typography>
          </AnimatedContent>
        </Box>

        {/* Cards grid */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 4, md: 5 } }}>
          <Grid container spacing={2.5}>
            {newsSection.tiles.map((item, i) => {
              const cardThemes = [
                { gradient: 'linear-gradient(135deg, #d4af37, #e5c158)', accent: '#d4af37', glow: 'rgba(212,175,55,0.35)', textAccent: '#fff8e1', iconGradient: 'linear-gradient(135deg, #b8960c, #d4af37)', chipBg: 'rgba(212,175,55,0.15)', chipBorder: 'rgba(212,175,55,0.35)' },
                { gradient: 'linear-gradient(135deg, #e65100, #ff8f00)', accent: '#ff8f00', glow: 'rgba(230,81,0,0.35)', textAccent: '#fff3e0', iconGradient: 'linear-gradient(135deg, #bf360c, #e65100)', chipBg: 'rgba(255,143,0,0.15)', chipBorder: 'rgba(255,143,0,0.35)' },
                { gradient: 'linear-gradient(135deg, #c62828, #e53935)', accent: '#e53935', glow: 'rgba(198,40,40,0.35)', textAccent: '#ffcdd2', iconGradient: 'linear-gradient(135deg, #8e0000, #c62828)', chipBg: 'rgba(229,57,53,0.15)', chipBorder: 'rgba(229,57,53,0.35)' },
                { gradient: 'linear-gradient(135deg, #ff6f00, #ffa000)', accent: '#ffa000', glow: 'rgba(255,111,0,0.35)', textAccent: '#fff8e1', iconGradient: 'linear-gradient(135deg, #e65100, #ff6f00)', chipBg: 'rgba(255,160,0,0.15)', chipBorder: 'rgba(255,160,0,0.35)' },
              ];
              const CardIcons = [CampaignIcon, NewspaperIcon, CalendarMonthIcon, GavelIcon];
              const CardIcon = CardIcons[i] || AnnouncementIcon;
              const cc = cardThemes[i] || cardThemes[0];
              return (
                <Grid item xs={12} sm={6} md={3} key={item.title || i}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4, overflow: 'hidden',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(212,175,55,0.12)',
                      backdropFilter: 'blur(12px)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      height: '100%', display: 'flex', flexDirection: 'column',
                      position: 'relative',
                      '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(180deg, rgba(212,175,55,0.03) 0%, transparent 40%)',
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 50px ${cc.glow}, 0 0 0 1px ${cc.accent}30`,
                        bgcolor: 'rgba(255,255,255,0.07)',
                        borderColor: `${cc.accent}50`,
                        '& .news-icon-box': { transform: 'scale(1.1) rotate(-5deg)', boxShadow: `0 8px 25px ${cc.glow}` },
                        '& .news-shine': { left: '120%' },
                        '& .news-arrow': { transform: 'translateX(6px)', opacity: 1 },
                        '& .news-top-bar': { height: 5 },
                      },
                    }}
                  >
                    {/* Top sacred gradient bar */}
                    <Box className="news-top-bar" sx={{
                      height: 4, background: cc.gradient,
                      position: 'relative', overflow: 'hidden',
                      transition: 'height 0.3s ease',
                      boxShadow: `0 2px 8px ${cc.glow}`,
                    }}>
                      <Box className="news-shine" sx={{
                        position: 'absolute', top: 0, left: '-40%', width: '40%', height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                        transition: 'left 0.6s ease',
                      }} />
                    </Box>

                    <Box sx={{ p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Temple-style icon with sacred gradient */}
                      <Box className="news-icon-box" sx={{
                        width: 54, height: 54, borderRadius: 3,
                        background: cc.iconGradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                        boxShadow: `0 6px 20px ${cc.glow}`,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        border: '1px solid rgba(255,255,255,0.15)',
                        '&::after': {
                          content: '""', position: 'absolute', inset: 0, borderRadius: 3,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.25), transparent 50%)',
                        },
                      }}>
                        <CardIcon sx={{ color: '#fff', fontSize: 24, position: 'relative', zIndex: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
                      </Box>

                      {/* Title + sacred chip */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#fff', letterSpacing: 0.3 }}>
                          {item.title}
                        </Typography>
                        <Box sx={{
                          px: 1.2, py: 0.3, borderRadius: 6,
                          bgcolor: cc.chipBg, border: `1px solid ${cc.chipBorder}`,
                          fontSize: 10, fontWeight: 800, color: cc.textAccent,
                          letterSpacing: 0.8, textTransform: 'uppercase',
                        }}>
                          {item.chip}
                        </Box>
                      </Box>

                      {/* Description */}
                      <Typography sx={{ color: 'rgba(255,224,178,0.6)', fontSize: '0.92rem', lineHeight: 1.7, mb: 3, flex: 1 }}>
                        {item.description}
                      </Typography>

                      {/* Divider with temple pattern */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${cc.accent}30, transparent)` }} />
                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${cc.accent}40` }} />
                        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${cc.accent}30)` }} />
                      </Box>

                      {/* Bottom action */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Button
                          component={RouterLink} to={item.link} size="small"
                          sx={{
                            color: cc.textAccent, fontWeight: 700, px: 0, textTransform: 'none',
                            fontSize: '0.9rem',
                            '&:hover': { bgcolor: 'transparent', color: '#fff' },
                          }}
                        >
                          Read More
                        </Button>
                        <Box className="news-arrow" sx={{ color: cc.textAccent, fontSize: 16, fontWeight: 700, opacity: 0.5, transition: 'all 0.3s ease' }}>→</Box>
                      </Box>
                    </Box>

                    {/* Corner deepam glow */}
                    <Box sx={{
                      position: 'absolute', bottom: -25, right: -25, width: 80, height: 80, borderRadius: '50%',
                      background: `radial-gradient(circle, ${cc.accent}08, transparent 70%)`,
                      pointerEvents: 'none',
                    }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Sacred bottom strip with deepam indicators */}
          <Box sx={{
            mt: 3, display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap',
            px: { xs: 1.5, md: 2.5 }, py: 2, borderRadius: 3,
            bgcolor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)',
            position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1,
              background: 'linear-gradient(90deg, transparent 10%, rgba(212,175,55,0.3) 50%, transparent 90%)',
            },
          }}>
            {[
              { label: 'Latest Updates', value: 'Temple bulletins & circulars', color: '#d4af37', icon: '🪔' },
              { label: 'Event Calendar', value: 'Upcoming utsavams & sevas', color: '#e65100', icon: '📿' },
              { label: 'Stay Connected', value: 'Never miss an announcement', color: '#c62828', icon: '🔔' },
            ].map((stat, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: { md: 1 } }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 2, bgcolor: `${stat.color}15`,
                  border: `1px solid ${stat.color}25`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>
                  {stat.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: 'rgba(212,175,55,0.6)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>{stat.label}</Typography>
                  <Typography sx={{ color: 'rgba(255,224,178,0.7)', fontSize: '0.9rem', fontWeight: 600 }}>{stat.value}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom sacred border */}
        <Box sx={{ height: 3, background: 'linear-gradient(90deg, #d4af37, #e65100, #c62828, #e65100, #d4af37)', opacity: 0.5 }} />
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  DONATION OVERVIEW — Deep sacred teal & copper patina     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={150} staggerChildren={true}>
      <Box sx={{
        mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(160deg, #031c19 0%, #08302a 25%, #0c3f36 50%, #072e27 75%, #021a17 100%)',
        boxShadow: '0 30px 80px rgba(3,28,25,0.5)',
      }}>
        {/* Sacred ambient glows — teal & copper */}
        <Box sx={{ position: 'absolute', top: -120, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,137,123,0.1), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -80, width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(205,127,50,0.07), transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '50%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(77,182,172,0.04), transparent 50%)', pointerEvents: 'none', transform: 'translateX(-50%)' }} />

        {/* Top sacred band — temple pond mosaic */}
        <Box sx={{ height: 6, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', background: 'repeating-linear-gradient(90deg, #00897b 0px, #00897b 8px, transparent 8px, transparent 12px, #cd7f32 12px, #cd7f32 20px, transparent 20px, transparent 24px, #4db6ac 24px, #4db6ac 32px, transparent 32px, transparent 36px)', opacity: 0.6 }} />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #031c19 0%, transparent 10%, transparent 90%, #031c19 100%)' }} />
        </Box>

        {/* Floating sacred lotus particles */}
        {[
          { top: 25, left: 50, delay: '0s', char: '❋' },
          { top: 65, right: 70, delay: '1s', char: '✦' },
          { bottom: 130, left: '28%', delay: '1.5s', char: '❋' },
          { top: 130, right: '22%', delay: '0.5s', char: '✦' },
          { bottom: 50, right: 100, delay: '2s', char: '❋' },
          { top: '50%', left: 35, delay: '2.5s', char: '✦' },
        ].map((p, idx) => (
          <Box key={`lp-${idx}`} sx={{
            position: 'absolute', top: p.top, right: p.right, bottom: p.bottom, left: p.left,
            fontSize: 8, color: '#4db6ac',
            animation: `lotusFloat ${3 + idx * 0.5}s ease-in-out infinite`,
            animationDelay: p.delay, pointerEvents: 'none',
            '@keyframes lotusFloat': {
              '0%, 100%': { opacity: 0.15, transform: 'scale(1) translateY(0) rotate(0deg)' },
              '50%': { opacity: 0.5, transform: 'scale(1.4) translateY(-10px) rotate(180deg)' },
            },
          }}>{p.char}</Box>
        ))}

        {/* Sacred tulsi watermarks */}
        {[
          { top: 50, right: 40, size: 70, opacity: 0.025 },
          { bottom: 80, left: 50, size: 90, opacity: 0.02 },
        ].map((om, idx) => (
          <Box key={`tw-${idx}`} sx={{
            position: 'absolute', top: om.top, right: om.right, bottom: om.bottom, left: om.left,
            fontSize: om.size, color: '#4db6ac', opacity: om.opacity, fontWeight: 900,
            pointerEvents: 'none', userSelect: 'none', lineHeight: 1,
            animation: `tFloat ${9 + idx * 3}s ease-in-out infinite`,
            '@keyframes tFloat': { '0%, 100%': { transform: 'rotate(0deg) scale(1)' }, '50%': { transform: 'rotate(5deg) scale(1.05)' } },
          }}>ॐ</Box>
        ))}

        {/* ===== HEADER ===== */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 3.5, md: 4.5 }, pb: { xs: 1.5, md: 2 }, position: 'relative', zIndex: 1 }}>
          <SectionOrnament variant="om" opacity={0.025} size={100} color="#4db6ac" repeat={3} offset={-80} blendMode="normal" />

          <AnimatedContent delay={100}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2,
              px: 2.5, py: 0.8, borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0,137,123,0.1), rgba(77,182,172,0.04))',
              border: '1px solid rgba(77,182,172,0.25)',
            }}>
              <VolunteerActivismIcon sx={{ color: '#4db6ac', fontSize: 16 }} />
              <Typography sx={{ color: '#4db6ac', fontWeight: 800, letterSpacing: 3, fontSize: 12, textTransform: 'uppercase' }}>
                {donationSection.subtitle}
              </Typography>
            </Box>
          </AnimatedContent>

          <AnimatedContent delay={250}>
            <Typography variant="h3" sx={{
              fontWeight: 900, mb: 1.5, lineHeight: 1.15,
              fontSize: { xs: '1.6rem', md: '2.3rem' },
              background: 'linear-gradient(135deg, #e0f2f1 0%, #80cbc4 40%, #4db6ac 70%, #cd7f32 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {donationSection.title}
            </Typography>
          </AnimatedContent>

          <AnimatedContent delay={400}>
            <Typography sx={{ color: 'rgba(178,223,219,0.6)', maxWidth: 520, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>
              {donationSection.description}
            </Typography>
          </AnimatedContent>

          {/* Decorative divider — copper & teal */}
          <AnimatedContent delay={500}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5, mb: 1, maxWidth: 200 }}>
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #4db6ac, transparent)' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#cd7f32', boxShadow: '0 0 8px rgba(205,127,50,0.5)' }} />
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(77,182,172,0.3))' }} />
            </Box>
          </AnimatedContent>
        </Box>

        {/* ===== DONATION CAUSE CARDS ===== */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
          <Grid container spacing={2.5}>
            {donationSection.tiles.map((item, i) => {
              const themes = [
                { iconGradient: 'linear-gradient(135deg, #00695c, #00897b)', accent: '#26a69a', glow: 'rgba(0,137,123,0.3)', progressBg: 'linear-gradient(90deg, #00695c, #00897b, #26a69a)', tagBg: 'rgba(38,166,154,0.12)', tagBorder: 'rgba(38,166,154,0.3)', tagText: '#b2dfdb', cardBg: 'rgba(0,137,123,0.04)' },
                { iconGradient: 'linear-gradient(135deg, #a0651e, #cd7f32)', accent: '#cd7f32', glow: 'rgba(205,127,50,0.3)', progressBg: 'linear-gradient(90deg, #a0651e, #cd7f32, #daa520)', tagBg: 'rgba(205,127,50,0.12)', tagBorder: 'rgba(205,127,50,0.3)', tagText: '#ffe0b2', cardBg: 'rgba(205,127,50,0.04)' },
                { iconGradient: 'linear-gradient(135deg, #00796b, #009688)', accent: '#4db6ac', glow: 'rgba(77,182,172,0.25)', progressBg: 'linear-gradient(90deg, #00796b, #009688, #4db6ac)', tagBg: 'rgba(77,182,172,0.12)', tagBorder: 'rgba(77,182,172,0.3)', tagText: '#e0f2f1', cardBg: 'rgba(77,182,172,0.04)' },
                { iconGradient: 'linear-gradient(135deg, #8d6e2f, #b8860b)', accent: '#daa520', glow: 'rgba(218,165,32,0.25)', progressBg: 'linear-gradient(90deg, #8d6e2f, #b8860b, #daa520)', tagBg: 'rgba(218,165,32,0.12)', tagBorder: 'rgba(218,165,32,0.3)', tagText: '#fff8e1', cardBg: 'rgba(218,165,32,0.03)' },
              ];
              const CardIcons = [CreditScoreIcon, RestaurantIcon, ConstructionIcon, FestivalIcon];
              const CardIcon = CardIcons[i] || PaymentIcon;
              const tc = themes[i] || themes[0];
              return (
                <Grid item xs={12} sm={6} key={item.title || i}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4, overflow: 'hidden', height: '100%',
                      bgcolor: tc.cardBg,
                      border: '1px solid rgba(77,182,172,0.1)',
                      backdropFilter: 'blur(12px)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative', display: 'flex', flexDirection: 'column',
                      '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: `linear-gradient(180deg, ${tc.cardBg} 0%, transparent 40%)`,
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 24px 50px ${tc.glow}, 0 0 0 1px ${tc.accent}25`,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderColor: `${tc.accent}40`,
                        '& .don-icon': { transform: 'scale(1.12) rotate(-5deg)', boxShadow: `0 8px 28px ${tc.glow}` },
                        '& .don-cta': { background: tc.iconGradient, color: '#fff', transform: 'translateY(-1px)', boxShadow: `0 6px 20px ${tc.glow}` },
                      },
                    }}
                  >
                    {/* Left accent strip */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: tc.iconGradient, boxShadow: `2px 0 10px ${tc.glow}` }} />

                    <Box sx={{ p: { xs: 2.5, md: 3 }, pl: { xs: 3.5, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Icon + Title row */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                        <Box className="don-icon" sx={{
                          width: 54, height: 54, borderRadius: '50%', flexShrink: 0,
                          background: tc.iconGradient,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: `0 4px 16px ${tc.glow}`,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: '2px solid rgba(255,255,255,0.1)',
                          position: 'relative',
                          '&::after': {
                            content: '""', position: 'absolute', inset: 0, borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.25), transparent 50%)',
                          },
                        }}>
                          <CardIcon sx={{ color: '#fff', fontSize: 24, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))', position: 'relative', zIndex: 1 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#e0f2f1', letterSpacing: 0.2 }}>
                              {item.title}
                            </Typography>
                            <Box sx={{
                              px: 1.2, py: 0.3, borderRadius: 6,
                              bgcolor: tc.tagBg, border: `1px solid ${tc.tagBorder}`,
                              fontSize: 12, fontWeight: 800, color: tc.tagText,
                              letterSpacing: 0.8, textTransform: 'uppercase',
                            }}>
                              {item.tag}
                            </Box>
                          </Box>
                          <Typography sx={{ color: 'rgba(178,223,219,0.55)', fontSize: '0.92rem', lineHeight: 1.7 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Divider */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${tc.accent}25, transparent)` }} />
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: `${tc.accent}35` }} />
                        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${tc.accent}25)` }} />
                      </Box>

                      {/* CTA button */}
                      <Button
                        className="don-cta"
                        component={RouterLink} to={item.link} variant="contained" size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          background: `${tc.accent}20`, color: tc.tagText,
                          fontWeight: 700, borderRadius: 2, px: 3, py: 0.8,
                          textTransform: 'none', fontSize: '0.9rem', border: `1px solid ${tc.accent}30`,
                          transition: 'all 0.3s ease', boxShadow: 'none',
                          '&:hover': { color: '#fff' },
                        }}
                      >
                        Contribute Now
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Trust strip */}
        <Box sx={{
          mx: { xs: 2, md: 4 }, mb: { xs: 3, md: 4 },
          display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap',
          px: { xs: 1.5, md: 2.5 }, py: 2, borderRadius: 3,
          bgcolor: 'rgba(0,137,123,0.04)', border: '1px solid rgba(77,182,172,0.1)',
          position: 'relative', overflow: 'hidden',
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1,
            background: 'linear-gradient(90deg, transparent 10%, rgba(77,182,172,0.2) 50%, transparent 90%)',
          },
        }}>
          {[
            { IconComp: LockIcon, label: 'Secure Payments', value: 'Razorpay protected', color: '#26a69a' },
            { IconComp: VerifiedIcon, label: 'Temple Verified', value: 'Official donations only', color: '#cd7f32' },
            { IconComp: DescriptionIcon, label: 'Tax Benefits', value: 'Receipts provided', color: '#4db6ac' },
          ].map((info, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: { md: 1 } }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 2,
                bgcolor: `${info.color}10`, border: `1px solid ${info.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}><info.IconComp sx={{ color: info.color, fontSize: 16 }} /></Box>
              <Box>
                <Typography sx={{ color: 'rgba(77,182,172,0.55)', fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>{info.label}</Typography>
                <Typography sx={{ color: 'rgba(178,223,219,0.6)', fontSize: '0.9rem', fontWeight: 600 }}>{info.value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom sacred border — teal & copper */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #00897b, #cd7f32, #4db6ac, #cd7f32, #00897b, transparent)', opacity: 0.5 }} />
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  GALLERY OVERVIEW — Dark immersive purple theme           */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={200} staggerChildren={true}>
      <Box sx={{
        mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 50%, #1a1a2e 100%)',
        position: 'relative',
        boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Ambient glows */}
        <Box sx={{ position: 'absolute', top: -60, left: -60, width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(106,27,154,0.15), transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, right: -60, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(179,157,219,0.08), transparent 70%)', pointerEvents: 'none' }} />

        {/* Top accent bar */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #7c4dff, #b39ddb, #e040fb, #7c4dff, transparent)', opacity: 0.5 }} />

        {/* Header */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 4, md: 5 }, pb: { xs: 2, md: 2 }, position: 'relative', zIndex: 1 }}>
          <AnimatedContent delay={100} direction="right">
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2,
              px: 2.5, py: 0.8, borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(179,157,219,0.1), rgba(179,157,219,0.03))',
              border: '1px solid rgba(179,157,219,0.25)',
            }}>
              <PhotoLibraryIcon sx={{ color: '#b39ddb', fontSize: 16 }} />
              <Typography sx={{ color: '#b39ddb', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
                {gallerySection.subtitle}
              </Typography>
            </Box>
          </AnimatedContent>
          <AnimatedContent delay={250} direction="right">
            <Typography variant="h3" sx={{
              fontWeight: 900, mb: 1.5, lineHeight: 1.15,
              fontSize: { xs: '1.6rem', md: '2.3rem' },
              background: 'linear-gradient(135deg, #fff 0%, #e1bee7 50%, #b39ddb 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {gallerySection.title}
            </Typography>
          </AnimatedContent>
          <AnimatedContent delay={400} direction="right">
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, maxWidth: 520, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>
              {gallerySection.description}
            </Typography>
          </AnimatedContent>
          <AnimatedContent delay={500} direction="right">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1, maxWidth: 200 }}>
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #b39ddb, transparent)' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#7c4dff', boxShadow: '0 0 8px rgba(124,77,255,0.5)' }} />
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(179,157,219,0.3))' }} />
            </Box>
          </AnimatedContent>
        </Box>

        {/* ===== ROW 1 — Photos from Firebase ===== */}
        {galleryPhotos.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 1 }}>
            <Box sx={{ width: 3, height: 16, bgcolor: '#b39ddb', borderRadius: 2, boxShadow: '0 0 6px rgba(179,157,219,0.4)' }} />
            <Typography sx={{ color: 'rgba(179,157,219,0.7)', fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>
              📸 Photo Gallery
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {galleryPhotos.slice(0, 8).map((photo, i) => {
              const photoAccents = ['#b39ddb', '#7c4dff', '#e040fb', '#ce93d8'];
              const accent = photoAccents[i % photoAccents.length];
              return (
                <Grid item xs={6} sm={4} md={3} key={photo.id}>
                  <Paper
                    component={RouterLink} to="/gallery/photos"
                    elevation={0}
                    sx={{
                      borderRadius: 4, overflow: 'hidden',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(179,157,219,0.12)',
                      textDecoration: 'none', display: 'block',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: `0 20px 50px rgba(124,77,255,0.2)`,
                        borderColor: `${accent}50`,
                        '& .gallery-img': { transform: 'scale(1.08)' },
                        '& .gallery-overlay': { opacity: 1 },
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <Box
                        className="gallery-img"
                        component="img"
                        src={photo.imageUrl}
                        alt={photo.title}
                        sx={{ width: '100%', height: { xs: 140, md: 200 }, objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }}
                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <Box className="gallery-overlay" sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(180deg, transparent 40%, rgba(26,26,46,0.85) 100%)',
                        opacity: 0, transition: 'opacity 0.3s ease',
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 2,
                      }}>
                        <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>
                          View Gallery →
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, transparent)`, opacity: 0.7 }} />
                    </Box>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.75rem', md: '0.85rem' } }} noWrap>{photo.title}</Typography>
                      {photo.category && (
                        <Box sx={{
                          px: 1.2, py: 0.3, borderRadius: 6,
                          bgcolor: `${accent}18`, border: `1px solid ${accent}30`,
                          fontSize: 10, fontWeight: 800, color: accent,
                          letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0, ml: 1,
                        }}>{photo.category}</Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        )}

        {/* ===== ROW 2 — Videos from Firebase ===== */}
        {galleryVideos.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 1 }}>
            <Box sx={{ width: 3, height: 16, bgcolor: '#e040fb', borderRadius: 2, boxShadow: '0 0 6px rgba(224,64,251,0.4)' }} />
            <Typography sx={{ color: 'rgba(224,64,251,0.7)', fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>
              🎬 Video Highlights
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {galleryVideos.slice(0, 4).map((video, i) => {
              const videoAccents = ['#e040fb', '#ea80fc', '#7c4dff', '#b388ff'];
              const accent = videoAccents[i % videoAccents.length];
              const embedUrl = (() => {
                let vid = '';
                const wm = video.videoUrl.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
                const sm = video.videoUrl.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
                const em = video.videoUrl.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
                const sh = video.videoUrl.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
                if (wm) vid = wm[1]; else if (sm) vid = sm[1]; else if (em) vid = em[1]; else if (sh) vid = sh[1];
                return vid ? `https://www.youtube.com/embed/${vid}` : video.videoUrl;
              })();
              const thumbnailId = embedUrl.match(/\/embed\/([a-zA-Z0-9_-]{11})/)?.[1];
              return (
                <Grid item xs={12} sm={6} md={3} key={video.id || i}>
                  <Paper
                    component={RouterLink} to="/gallery/videos"
                    elevation={0}
                    sx={{
                      borderRadius: 4, overflow: 'hidden',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(224,64,251,0.12)',
                      textDecoration: 'none', display: 'block',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 50px rgba(224,64,251,0.15)',
                        borderColor: `${accent}50`,
                        '& .video-play': { transform: 'scale(1.15)', boxShadow: `0 0 30px ${accent}60` },
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      {thumbnailId ? (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            component="img"
                            src={`https://img.youtube.com/vi/${thumbnailId}/hqdefault.jpg`}
                            alt={video.title}
                            sx={{ width: '100%', height: { xs: 150, md: 210 }, objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                            <Box className="video-play" sx={{
                              width: 50, height: 50, borderRadius: '50%',
                              bgcolor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: `0 4px 20px ${accent}40`,
                              transition: 'all 0.3s ease',
                            }}>
                              <Box sx={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '9px 0 9px 16px', borderColor: `transparent transparent transparent #fff`, ml: 0.5 }} />
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: { xs: 150, md: 210 }, bgcolor: '#2d1b4e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>No thumbnail</Typography>
                        </Box>
                      )}
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, transparent)`, opacity: 0.7 }} />
                    </Box>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.8rem', md: '0.9rem' } }} noWrap>{video.title}</Typography>
                      <Box sx={{
                        px: 1.2, py: 0.3, borderRadius: 6,
                        bgcolor: `${accent}18`, border: `1px solid ${accent}30`,
                        fontSize: 10, fontWeight: 800, color: accent,
                        letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0, ml: 1,
                      }}>Video</Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        )}

        {/* ===== Gallery stats strip ===== */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 4, md: 5 } }}>
          <Box sx={{
            display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap',
            px: { xs: 2, md: 3 }, py: 2.5, borderRadius: 3,
            bgcolor: 'rgba(179,157,219,0.04)', border: '1px solid rgba(179,157,219,0.1)',
            position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1,
              background: 'linear-gradient(90deg, transparent 10%, rgba(179,157,219,0.2) 50%, transparent 90%)',
            },
          }}>
            {[
              { icon: '📸', label: 'Photos', value: `${galleryPhotos.length} photos uploaded`, color: '#b39ddb' },
              { icon: '📂', label: 'Categories', value: 'Events, Premises, Festivals', color: '#7c4dff' },
              { icon: '🎬', label: 'Videos', value: `${galleryVideos.length} videos available`, color: '#e040fb' },
            ].map((info, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: { md: 1 } }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 2,
                  bgcolor: `${info.color}12`, border: `1px solid ${info.color}25`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>{info.icon}</Box>
                <Box>
                  <Typography sx={{ color: 'rgba(179,157,219,0.5)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>{info.label}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', fontWeight: 600 }}>{info.value}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom accent bar */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #7c4dff, #e040fb, #b39ddb, #e040fb, #7c4dff, transparent)', opacity: 0.45 }} />
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  NEARBY OVERVIEW — Immersive image cards with glass overlay */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={250} staggerChildren={true}>
      <Box sx={{
        mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden',
        background: 'linear-gradient(165deg, #0a1f1c 0%, #132e28 40%, #1a3d33 70%, #0d2922 100%)',
        p: { xs: 3, md: 5 }, position: 'relative',
        border: '1px solid rgba(0,121,107,0.15)',
      }}>
        {/* Ambient background glow */}
        <Box sx={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,150,136,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(76,175,80,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <SectionOrnament variant="om" opacity={0.06} size={90} color="#4db6ac" repeat={3} offset={-80} blendMode="normal" />

        <AnimatedContent delay={100}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 4, height: 28, background: 'linear-gradient(180deg, #4db6ac, #00796b)', borderRadius: 2, boxShadow: '0 0 12px rgba(77,182,172,0.3)' }} />
            <Typography sx={{ color: 'rgba(77,182,172,0.8)', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
              {nearbySection.subtitle}
            </Typography>
          </Box>
        </AnimatedContent>
        <AnimatedContent delay={250}>
          <Typography variant="h4" sx={{
            fontWeight: 800, mb: 0.5,
            background: 'linear-gradient(135deg, #e0f2f1, #b2dfdb, #80cbc4)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {nearbySection.title}
          </Typography>
        </AnimatedContent>
        <AnimatedContent delay={400}>
          <Typography sx={{ color: 'rgba(178,223,219,0.65)', mb: 4, maxWidth: 560, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>{nearbySection.description}</Typography>
        </AnimatedContent>

        {/* === Top row: 3 large cards === */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {nearbySection.tiles.slice(0, 3).map((item, i) => {
            const NearbyIcons = [BeachAccessIcon, TempleIcon, LandscapeIcon];
            const IconComp = NearbyIcons[i] || BeachAccessIcon;
            const accents = ['#4dd0e1', '#ffab40', '#69f0ae'];
            const accent = accents[i];
            return (
              <Grid item xs={12} sm={6} md={4} key={item.title || i}>
                <Paper
                  component={RouterLink} to={item.link}
                  elevation={0}
                  sx={{
                    borderRadius: 4, overflow: 'hidden', position: 'relative',
                    height: { xs: 220, md: 280 },
                    textDecoration: 'none', display: 'block',
                    border: '1px solid rgba(77,182,172,0.1)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: `0 24px 60px rgba(0,0,0,0.4), 0 0 40px ${accent}15`,
                      borderColor: `${accent}40`,
                      '& .nearby-img': { transform: 'scale(1.12)', filter: 'brightness(0.55)' },
                      '& .nearby-glass': { backdropFilter: 'blur(12px)' },
                      '& .nearby-arrow': { transform: 'translateX(4px)', opacity: 1 },
                      '& .nearby-chip': { background: accent, color: '#0a1f1c' },
                    },
                  }}
                >
                  {/* Background image */}
                  <Box
                    className="nearby-img"
                    sx={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url(${(item as any).imageUrl?.startsWith('http') ? (item as any).imageUrl : `${process.env.PUBLIC_URL}${(item as any).imageUrl || ''}`})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: 'brightness(0.65)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, transparent 20%, rgba(10,31,28,0.6) 55%, rgba(10,31,28,0.92) 100%)` }} />
                  {/* Top accent line */}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, transparent 70%)`, opacity: 0.8, zIndex: 2 }} />

                  {/* Content overlay */}
                  <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: { xs: 2.5, md: 3 } }}>
                    {/* Top: chip + icon */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box className="nearby-chip" sx={{
                        px: 1.5, py: 0.4, borderRadius: 6,
                        background: 'rgba(255,255,255,0.12)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        fontSize: 10, fontWeight: 800, color: accent,
                        letterSpacing: 1, textTransform: 'uppercase',
                        transition: 'all 0.3s ease',
                      }}>
                        {item.chip}
                      </Box>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <IconComp sx={{ color: accent, fontSize: 20 }} />
                      </Box>
                    </Box>

                    {/* Bottom: title + description + arrow */}
                    <Box className="nearby-glass">
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.3rem' }, mb: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem', lineHeight: 1.6, maxWidth: '80%' }}>
                          {item.description}
                        </Typography>
                        <Typography className="nearby-arrow" sx={{ color: accent, fontWeight: 700, fontSize: { xs: '1rem', md: '1.2rem' }, opacity: 0.6, transition: 'all 0.3s ease', flexShrink: 0 }}>
                          →
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* === Bottom row: 3 smaller cards === */}
        <Grid container spacing={2.5}>
          {nearbySection.tiles.slice(3, 6).map((item, i) => {
            const NearbyIcons2 = [MuseumIcon, DirectionsBoatIcon, HikingIcon];
            const IconComp = NearbyIcons2[i] || MuseumIcon;
            const accents2 = ['#ce93d8', '#4dd0e1', '#ffd54f'];
            const accent = accents2[i];
            return (
              <Grid item xs={12} sm={6} md={4} key={item.title || i}>
                <Paper
                  component={RouterLink} to={item.link}
                  elevation={0}
                  sx={{
                    borderRadius: 4, overflow: 'hidden', position: 'relative',
                    height: { xs: 180, md: 220 },
                    textDecoration: 'none', display: 'block',
                    border: '1px solid rgba(77,182,172,0.08)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: `0 24px 60px rgba(0,0,0,0.4), 0 0 40px ${accent}15`,
                      borderColor: `${accent}40`,
                      '& .nearby-img2': { transform: 'scale(1.12)', filter: 'brightness(0.5)' },
                      '& .nearby-arrow2': { transform: 'translateX(4px)', opacity: 1 },
                      '& .nearby-chip2': { background: accent, color: '#0a1f1c' },
                    },
                  }}
                >
                  {/* Background image */}
                  <Box
                    className="nearby-img2"
                    sx={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url(${(item as any).imageUrl?.startsWith('http') ? (item as any).imageUrl : `${process.env.PUBLIC_URL}${(item as any).imageUrl || ''}`})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: 'brightness(0.6)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, transparent 10%, rgba(10,31,28,0.55) 50%, rgba(10,31,28,0.9) 100%)` }} />
                  {/* Top accent */}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, transparent 70%)`, opacity: 0.7, zIndex: 2 }} />

                  {/* Content */}
                  <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box className="nearby-chip2" sx={{
                        px: 1.5, py: 0.4, borderRadius: 6,
                        background: 'rgba(255,255,255,0.12)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        fontSize: 10, fontWeight: 800, color: accent,
                        letterSpacing: 1, textTransform: 'uppercase',
                        transition: 'all 0.3s ease',
                      }}>
                        {item.chip}
                      </Box>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <IconComp sx={{ color: accent, fontSize: 18 }} />
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.15rem' }, mb: 0.3, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem', lineHeight: 1.6, maxWidth: '82%' }}>
                          {item.description}
                        </Typography>
                        <Typography className="nearby-arrow2" sx={{ color: accent, fontWeight: 700, fontSize: { xs: '0.92rem', md: '1.1rem' }, opacity: 0.5, transition: 'all 0.3s ease', flexShrink: 0 }}>
                          →
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Stats strip */}
        <Box sx={{
          mt: 4, pt: 3, borderTop: '1px solid rgba(77,182,172,0.12)',
          display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 6 }, flexWrap: 'wrap',
        }}>
          {[
            { value: '15+', label: 'Attractions' },
            { value: '10 km', label: 'Radius' },
            { value: '6', label: 'Categories' },
          ].map((stat, idx) => (
            <Box key={idx} sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#4db6ac', fontWeight: 900, fontSize: { xs: '1.3rem', md: '1.6rem' }, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography sx={{ color: 'rgba(178,223,219,0.4)', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mt: 0.5 }}>{stat.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  BUSINESS PROMOTIONS — Sponsored ads section               */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={280} staggerChildren={true}>
        <BusinessPromotions />
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  CONTACT OVERVIEW — Premium dark warm with glass cards     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={300} staggerChildren={true}>
      <Box sx={{
        mb: 4, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden',
        background: 'linear-gradient(160deg, #1a1410 0%, #2c2218 35%, #3a2d1f 60%, #2a1f14 85%, #1a1410 100%)',
        position: 'relative',
        border: '1px solid rgba(212,175,55,0.12)',
        boxShadow: '0 30px 80px rgba(26,20,16,0.5)',
      }}>
        {/* Ambient warm glows */}
        <Box sx={{ position: 'absolute', top: -80, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -70, left: -50, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(230,81,0,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '30%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.03) 0%, transparent 50%)', pointerEvents: 'none' }} />
        <SectionOrnament variant="om" opacity={0.03} size={90} color="#d4af37" repeat={3} offset={-80} blendMode="normal" />

        {/* Top accent band */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #d4af37, #e65100, #d4af37, transparent)', opacity: 0.4 }} />

        {/* Header */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: { xs: 4, md: 5 }, pb: { xs: 2, md: 2 }, position: 'relative', zIndex: 1 }}>
          <AnimatedContent delay={100} direction="right">
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2,
              px: 2.5, py: 0.8, borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(212,175,55,0.1), rgba(212,175,55,0.03))',
              border: '1px solid rgba(212,175,55,0.2)',
            }}>
              <PhoneIcon sx={{ color: '#d4af37', fontSize: 15 }} />
              <Typography sx={{ color: '#d4af37', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
                {contactSection.subtitle}
              </Typography>
            </Box>
          </AnimatedContent>
          <AnimatedContent delay={250} direction="right">
            <Typography variant="h3" sx={{
              fontWeight: 900, mb: 1.5, lineHeight: 1.15,
              fontSize: { xs: '1.6rem', md: '2.3rem' },
              background: 'linear-gradient(135deg, #fff 0%, #ffe0b2 50%, #d4af37 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {contactSection.title}
            </Typography>
          </AnimatedContent>
          <AnimatedContent delay={400} direction="right">
            <Typography sx={{ color: 'rgba(255,224,178,0.55)', mb: 1, maxWidth: 520, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>
              {contactSection.description}
            </Typography>
          </AnimatedContent>
          <AnimatedContent delay={500} direction="right">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1, maxWidth: 200 }}>
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #d4af37, transparent)' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e65100', boxShadow: '0 0 8px rgba(230,81,0,0.5)' }} />
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3))' }} />
            </Box>
          </AnimatedContent>
        </Box>

        {/* Contact Cards */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
          <Grid container spacing={2.5}>
            {contactSection.tiles.map((item, i) => {
              const ContactIcons = [AccessTimeIcon, LocationOnIcon, InfoRoundedIcon, RateReviewIcon];
              const IconComp = ContactIcons[i] || PhoneIcon;
              const accents = ['#d4af37', '#e65100', '#43a047', '#7c4dff'];
              const accent = accents[i];
              const iconBgs = [
                'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))',
                'linear-gradient(135deg, rgba(230,81,0,0.15), rgba(230,81,0,0.05))',
                'linear-gradient(135deg, rgba(67,160,71,0.15), rgba(67,160,71,0.05))',
                'linear-gradient(135deg, rgba(124,77,255,0.15), rgba(124,77,255,0.05))',
              ];
              return (
                <Grid item xs={12} sm={6} md={3} key={item.title || i}>
                  <Paper
                    component={RouterLink} to={item.link}
                    elevation={0}
                    sx={{
                      p: 0, borderRadius: 4, overflow: 'hidden', height: '100%',
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${accent}18`,
                      backdropFilter: 'blur(12px)',
                      textDecoration: 'none', display: 'flex', flexDirection: 'column',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: `linear-gradient(180deg, ${accent}08 0%, transparent 50%)`,
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: `0 24px 50px rgba(0,0,0,0.3), 0 0 30px ${accent}12`,
                        borderColor: `${accent}40`,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '& .contact-icon-ring': { transform: 'scale(1.12) rotate(8deg)', boxShadow: `0 0 25px ${accent}30` },
                        '& .contact-arrow': { transform: 'translateX(6px)', opacity: 1 },
                        '& .contact-glow': { opacity: 0.7, width: '50%' },
                      },
                    }}
                  >
                    {/* Top glow accent */}
                    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}60, transparent)`, boxShadow: `0 2px 12px ${accent}30` }} />

                    <Box sx={{ p: { xs: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      {/* Icon ring */}
                      <Box className="contact-icon-ring" sx={{
                        width: 72, height: 72, borderRadius: '50%', mb: 2.5, mt: 1,
                        background: iconBgs[i],
                        border: `2px solid ${accent}25`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&::after': {
                          content: '""', position: 'absolute', inset: -5, borderRadius: '50%',
                          border: `1px dashed ${accent}15`,
                        },
                      }}>
                        <IconComp sx={{ color: accent, fontSize: 30, filter: `drop-shadow(0 0 6px ${accent}40)` }} />
                      </Box>

                      {/* Title */}
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#fff', mb: 0.8, letterSpacing: 0.3 }}>
                        {item.title}
                      </Typography>

                      {/* Description */}
                      <Typography sx={{ color: 'rgba(255,224,178,0.55)', fontSize: '0.92rem', lineHeight: 1.7, mb: 2.5, flex: 1 }}>
                        {item.description}
                      </Typography>

                      {/* Bottom glow line */}
                      <Box className="contact-glow" sx={{
                        width: '25%', height: 2, borderRadius: 1, mb: 1.5,
                        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                        opacity: 0.35, transition: 'all 0.4s ease',
                        boxShadow: `0 0 8px ${accent}30`,
                      }} />

                      {/* Arrow link */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Typography sx={{ color: accent, fontSize: '0.85rem', fontWeight: 700, letterSpacing: 0.5 }}>
                          View Details
                        </Typography>
                        <ArrowForwardIcon className="contact-arrow" sx={{ color: accent, fontSize: 14, transition: 'all 0.3s ease', opacity: 0.5 }} />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Bottom info strip */}
        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
          <Box sx={{
            display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 5 }, flexWrap: 'wrap',
            px: { xs: 2, md: 4 }, py: 2.5, borderRadius: 3,
            bgcolor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.08)',
            position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent 10%, rgba(212,175,55,0.15) 50%, transparent 90%)' },
          }}>
            {[
              { value: '6:00 – 11:00 AM', label: 'Kottayil Temple', color: '#d4af37' },
              { value: '5:30 AM – 8:30 AM', label: 'Kovilakam Morning', color: '#e65100' },
              { value: '5:30 – 7:30 PM', label: 'Kovilakam Evening', color: '#43a047' },
            ].map((stat, idx) => (
              <Box key={idx} sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: stat.color, fontWeight: 900, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1 }}>{stat.value}</Typography>
                <Typography sx={{ color: 'rgba(255,224,178,0.3)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', mt: 0.5 }}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom accent band */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, transparent, #e65100, #d4af37, #e65100, transparent)', opacity: 0.35 }} />
      </Box>
      </AnimatedSection>

      </Container>
    </Box>
  );
}
